/**
 * Prioritized search utility for game components
 * 
 * This utility provides search functionality that prioritizes results based on relevance:
 * 1. Prefix matches (items starting with the search term) appear first
 * 2. Substring matches (items containing the search term elsewhere) appear second
 * 
 * @param {Array} items - Array of items to search through
 * @param {string} searchTerm - The search term to filter by
 * @param {string} nameProperty - The property name to search on (default: 'name')
 * @param {Array} excludeItems - Array of items to exclude from results (default: [])
 * @param {number} maxResults - Maximum number of results to return (default: 10)
 * @returns {Array} Filtered and prioritized array of items
 */
export const prioritizedSearch = (
  items, 
  searchTerm, 
  nameProperty = 'name', 
  excludeItems = [], 
  maxResults = 10
) => {
  // Return empty array if no search term
  if (!searchTerm || searchTerm.trim().length === 0) {
    return [];
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  
  // Create exclude set for efficient lookup
  const excludeSet = new Set(
    (excludeItems || []).map(item => 
      typeof item === 'string' ? item.toLowerCase() : item[nameProperty]?.toLowerCase()
    )
  );

  // Filter out excluded items first
  const availableItems = (items || []).filter(item => {
    const itemName = item[nameProperty]?.toLowerCase();
    return itemName && !excludeSet.has(itemName);
  });

  // Split into prefix matches and substring matches
  const prefixMatches = [];
  const substringMatches = [];

  for (const item of availableItems) {
    const itemName = item[nameProperty]?.toLowerCase();
    if (!itemName) continue;

    if (itemName.startsWith(lowerSearchTerm)) {
      prefixMatches.push(item);
    } else if (itemName.includes(lowerSearchTerm)) {
      substringMatches.push(item);
    }
  }

  // Combine results with prefix matches first, then substring matches
  const prioritizedResults = [...prefixMatches, ...substringMatches];

  // Return limited results
  return prioritizedResults.slice(0, maxResults);
};

/**
 * Legacy function for backward compatibility
 * Use prioritizedSearch instead for new implementations
 */
export const filterItems = prioritizedSearch;